import React, { useEffect, useRef } from 'react';
import HomeHeader from '@/components/HomeHeader';
import Footer from '@/components/homepage/Footer';
import { usePageMeta } from '@/hooks/usePageMeta';
import TableOfContents from '@/components/resources/TableOfContents';
import EmailCapture from '@/components/resources/EmailCapture';

const TITLE = 'Single Missionary Support Raising: 9 Things That Are Different (and How to Handle Them)';
const DESCRIPTION =
    'A practical guide for single missionaries raising personal support — the nine things that work differently without a spouse\'s network, income, or story to lean on, and how to handle each one.';

const JSON_LD = {
    '@context': 'https://schema.org',
    '@graph': [
        {
            '@type': 'Article',
            headline: TITLE,
            description: DESCRIPTION,
            image: 'https://devotionfund.com/resources/cover22.jpg',
            datePublished: '2026-07-26',
            dateModified: '2026-07-26',
            author: { '@type': 'Organization', name: 'Devotion Fund', url: 'https://devotionfund.com' },
            publisher: {
                '@type': 'Organization',
                name: 'Devotion Fund',
                logo: { '@type': 'ImageObject', url: 'https://devotionfund.com/logo.png' },
            },
            mainEntityOfPage: {
                '@id': 'https://devotionfund.com/resources/single-missionary-support-raising',
                '@type': 'WebPage',
            },
        },
        {
            '@type': 'FAQPage',
            mainEntity: [
                {
                    '@type': 'Question',
                    name: 'Is it harder to raise support as a single missionary?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "It's different more than it's harder. A single missionary usually has one network and one income to draw from instead of two, so the total pool is smaller — but a single person can also move faster, decide alone, and often gets a level of individual attention from donors that a family appeal doesn't.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'How do single missionaries build a support team without a spouse\'s network?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Deliberately widen past your own immediate circle: your home church, college friends, past coworkers, small group, and family are all real starting points. The difference from a couple's appeal isn't the size of any one circle, it's that you only get to draw from your own — so start earlier and ask a wider range of people than you think you need to.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'How should a single missionary handle safety and privacy concerns donors raise?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Address it directly instead of waiting for someone to ask quietly. A short, honest paragraph on your support page or in your letter about your field living situation, team, and support network reassures donors who are specifically worried about a single person overseas — silence on the topic reads as bigger risk than plainly naming it.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'What should single missionaries say about loneliness and burnout to donors?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Naming it briefly and honestly — without turning every newsletter into a burden — builds trust rather than losing it. Donors who know a season was lonely and see you push through tend to become more committed, not less, because they've now seen the real cost of what their gift supports.",
                    },
                },
            ],
        },
    ],
};

export default function SingleMissionarySupportRaising() {
    usePageMeta(TITLE, DESCRIPTION, { path: '/resources/single-missionary-support-raising' });

    const articleRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const el = document.createElement('script');
        el.type = 'application/ld+json';
        el.setAttribute('data-single-missionary-jsonld', 'true');
        el.textContent = JSON.stringify(JSON_LD);
        document.head.appendChild(el);
        return () => {
            el.remove();
        };
    }, []);

    return (
        <div className="min-h-screen bg-white flex flex-col">
            <HomeHeader />
            <div className="w-full h-64 md:h-80 bg-gray-100 overflow-hidden">
                <img src="/resources/cover22.jpg" alt="Hands joined together, representing a missionary's support community" className="w-full h-full object-cover" />
            </div>
            <div className="max-w-3xl mx-auto px-4 py-12 w-full">
                <h1 className="text-3xl font-bold mb-6">{TITLE}</h1>
                <TableOfContents targetRef={articleRef} />
                <div className="prose max-w-none" ref={articleRef}>
                    <p>Almost every support-raising guide is written for a couple — "sit down with your spouse," "combine your networks," "tell your family's story." If you're raising support alone, half of that advice doesn't apply, and nobody tells you what to do instead. Here are the nine places single support-raising genuinely works differently, and what to do about each one.</p>

                    <h2>1. You have one network, not two</h2>

                    <p>A married couple raising support draws on two families, two friend groups, two churches (sometimes), and two work histories. As a single person, you only have your own. The fix isn't to panic about the smaller pool — it's to widen your definition of "network" earlier than a couple would need to: college roommates, past coworkers, your small group, your parents' friends, your childhood church. Most single missionaries under-ask because they assume their circle is too small, when the real issue is usually that they only asked the first, most obvious ring of people.</p>

                    <h2>2. You're making every decision alone</h2>

                    <p>There's no one across the table to help you decide on an ask amount, edit your letter, or debrief a hard conversation. That's a real gap, not a small one — so build a small, informal "board" on purpose: a mentor, a pastor, one or two peers doing the same thing. Loop them in before you send anything major. It replaces the built-in second opinion a couple has by default.</p>

                    <h2>3. Your story is your story, not a shared one</h2>

                    <p>A family appeal often leans on a shared narrative — "we felt called together." A single person's story is more personal and, done well, more specific: your own path to the field, in your own voice, without borrowing a "we" that isn't true. Don't dilute this into vague language to sound less alone. A clear, honest, first-person story ("here's why I, specifically, am doing this") reads as more compelling, not less, because it's harder to write and donors can tell the difference.</p>

                    <h2>4. Donors will ask about safety — answer before they ask</h2>

                    <p>Fairly or not, people worry more about a single person overseas than a family unit. Don't wait for someone to raise it awkwardly in a hallway conversation. A short, matter-of-fact paragraph in your letter or on your page — where you'll live, who's on your team, what your local support network looks like — closes the question before it becomes an unspoken reason someone hesitates to commit.</p>

                    <h2>5. Your monthly budget math is different, not automatically lower</h2>

                    <p>It's tempting to assume a single person's support goal is simply "half" of a family's. It isn't — housing, insurance, and travel don't scale down proportionally, and a single missionary often still needs a fully-funded number close to what a smaller family needs. Build your own budget line by line rather than anchoring off someone else's family number; see our guide on <a href="/resources/how-much-support-does-a-missionary-need">building a realistic monthly support budget</a> for the full category breakdown.</p>

                    <h2>6. Momentum feels slower because there's no second income to lean on</h2>

                    <p>A couple can sometimes bridge a gap in support with one spouse's outside income while they keep raising. Alone, there's usually no bridge — which makes the raising phase itself feel higher-stakes. Set a realistic timeline going in rather than measuring yourself against a couple's pace; our piece on <a href="/resources/how-long-to-raise-full-support-as-a-missionary">how long full support actually takes</a> breaks down what changes that timeline for anyone, single or not.</p>

                    <h2>7. Loneliness and burnout need to be named, not hidden</h2>

                    <p>Support-raising alone is a genuinely isolating season — no one else in the room understands the exact rejection, the exact slow "maybe," the exact fatigue of asking. Naming this briefly and honestly to a few close supporters, not hiding it behind constant positivity, tends to deepen commitment rather than scare people off. It also gives you somewhere to actually process it instead of carrying it silently.</p>

                    <h2>8. Your update rhythm needs to survive without a partner covering for you</h2>

                    <p>In a couple, one spouse often picks up the newsletter or the thank-you notes when the other is stretched thin. Alone, if you skip a season of updates, nothing quietly covers for you — donors just notice the silence. Build a simpler, more sustainable rhythm from day one (a short monthly update beats an ambitious one you'll abandon), and see our <a href="/resources/free-missionary-newsletter-template">newsletter template</a> for a format built to be maintained solo.</p>

                    <h2>9. You get to decide, and change course, faster</h2>

                    <p>The one real advantage: no coordinating a second person's schedule, calling, or opinion before you act. You can take an unplanned coffee meeting, adjust your ask, or follow up the same day a door opens. Use that speed deliberately — it's the one place single support-raising is genuinely easier, not just different.</p>

                    <h2>Frequently asked questions</h2>

                    <p><strong>Is it harder to raise support as a single missionary?</strong><br />
                    It's different more than harder — a smaller starting pool, but faster individual decisions and often closer donor attention.</p>

                    <p><strong>How do I build a support team without a spouse's network?</strong><br />
                    Widen past your first, most obvious circle earlier than a couple would need to — college friends, past coworkers, your small group, your parents' friends.</p>

                    <p><strong>How do I handle safety questions from donors?</strong><br />
                    Answer before they ask: a short, honest paragraph about your living situation, team, and local support network in your letter or page.</p>

                    <p><strong>What do I say to donors about loneliness or burnout?</strong><br />
                    Name it briefly and honestly to a few close supporters rather than hiding it — it tends to build trust and commitment, not lose it.</p>

                    <h2>Next reads on Devotion Fund</h2>
                    <ul>
                        <li><a href="/resources/how-much-support-does-a-missionary-need">How Much Support Does a Missionary Need? A Realistic Monthly Budget Breakdown</a></li>
                        <li><a href="/resources/how-long-to-raise-full-support-as-a-missionary">How Long Does It Take to Raise Full Support as a Missionary?</a></li>
                        <li><a href="/resources/free-missionary-newsletter-template">Free Missionary Newsletter Template + 3 Real Examples Teardown</a></li>
                    </ul>

                    <h2>About the author</h2>
                    <p>We're the team behind <a href="/">Devotion Fund</a>. This guide came out of conversations with single missionaries who kept describing the same nine gaps — the advice built for couples that simply didn't fit their season.</p>
                    <p><small><em>Last updated: 2026-07-26.</em></small></p>
                </div>
                <div className="mt-8">
                    <EmailCapture page="/resources/single-missionary-support-raising" />
                </div>
            </div>
            <Footer />
        </div>
    );
}
